import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bed, Bath, Maximize, Check, ArrowLeft, MapPin } from "lucide-react";
import { AREAS } from "../data";
import { Navbar } from "../components/Navbar";
import { AreaAlert } from "../components/AreaAlert";
import { Contact, Footer } from "../components/Contact";
import { Reveal } from "../components/Reveal";

export default function AreaPage() {
  const { slug } = useParams();
  const area = AREAS.find((a) => a.slug === slug);

  if (!area) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <h1 className="font-heading text-4xl font-light">Area not found</h1>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 text-gold hover:underline" data-testid="area-back-home">
          <ArrowLeft size={16} /> Back home
        </Link>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <main data-testid={`area-page-${area.slug}`}>
        {/* Hero */}
        <section className="relative min-h-[80vh] flex items-end">
          <div className="absolute inset-0">
            <img src={area.image} alt={`${area.name}, ${area.state}`} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/45" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-20 w-full">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="eyebrow text-white mb-5">
              {area.state} · {area.tagline}
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }} className="font-heading font-light text-white text-5xl sm:text-7xl tracking-tight leading-none">
              {area.name}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }} className="text-white/85 text-lg font-light max-w-xl mt-6">
              {area.blurb}
            </motion.p>
          </div>
        </section>

        {/* Intro + highlights */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="eyebrow text-gold mb-4">Living in {area.name}</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-light leading-tight">Why buyers love {area.name}</h2>
                <p className="text-[#52525b] font-light text-lg mt-6 leading-relaxed">{area.intro}</p>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-8">
                  {area.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-3 font-light">
                      <Check size={16} className="text-gold shrink-0 mt-1" /> {h}
                    </li>
                  ))}
                </ul>

                <div className="mt-10 pt-8 border-t border-black/5" data-testid="area-nearby">
                  <p className="eyebrow text-[11px] text-[#a1a1aa] mb-4">Also serving the surrounding areas</p>
                  <div className="flex flex-wrap gap-2.5">
                    {area.nearby.map((n) => (
                      <span key={n} className="inline-flex items-center gap-1.5 border border-black/10 px-3.5 py-1.5 text-sm font-light hover:border-gold transition-colors">
                        <MapPin size={13} className="text-gold" /> {n}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Recently sold card */}
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="bg-surface border border-black/5">
                  <div className="relative overflow-hidden aspect-[4/3]">
                    <img src={area.image} alt={`Sold home in ${area.name}`} className="w-full h-full object-cover" />
                    <span className="absolute top-4 left-4 bg-gold text-white eyebrow text-[11px] px-4 py-2">Sold</span>
                  </div>
                  <div className="p-7">
                    <p className="eyebrow text-[#a1a1aa] text-[11px]">{area.name}, {area.state}</p>
                    <p className="font-heading text-2xl mt-2">{area.price}</p>
                    <div className="flex items-center gap-6 mt-5 pt-5 border-t border-black/5 text-[#52525b] text-sm">
                      <span className="flex items-center gap-2"><Bed size={16} className="text-gold" /> {area.beds}</span>
                      <span className="flex items-center gap-2"><Bath size={16} className="text-gold" /> {area.baths}</span>
                      <span className="flex items-center gap-2"><Maximize size={16} className="text-gold" /> {area.sqft}</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Area-specific alert */}
        <section className="py-20 md:py-28 bg-[#0a0a0a]">
          <div className="max-w-5xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <p className="eyebrow text-gold mb-4">Buyer Alerts</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-light leading-tight text-white">
                New {area.name} homes, straight to your inbox
              </h2>
              <p className="text-white/70 font-light text-lg mt-6 leading-relaxed">
                Set it and forget it. We'll alert you the instant a home matching {area.name} hits the market.
              </p>
              <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white mt-8 text-sm transition-colors" data-testid="area-back">
                <ArrowLeft size={16} /> Explore all areas
              </Link>
            </Reveal>
            <Reveal delay={0.15}>
              <AreaAlert fixedArea={area.name} dark />
            </Reveal>
          </div>
        </section>

        <Contact />
      </main>
      <Footer />
    </>
  );
}
